"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  ExactToggle,
  GroupFields,
  PairFields,
  SingleFields,
} from "@/components/tools/stats-distributions-more/dataset-fields";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type Dataset,
  previewRows,
  type SingleDistribution,
  sampleCorrelation,
  summarizeColumn,
  summarizeGroups,
  toCsv,
} from "@/lib/tool-kit/stats-distributions-more/dataset";
import {
  buildDataset,
  type DatasetError,
  type DatasetForm,
  type DatasetInfo,
  type DatasetMode,
} from "@/lib/tool-kit/stats-distributions-more/dataset-form";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_DATASET_GENERATOR_STRINGS = {
  modeLabel: "What kind of data?",
  modePair: "Two correlated variables",
  modeGroups: "Groups with different means",
  modeSingle: "One variable",
  rowsLabel: "Rows",
  rowsGroupsLabel: "Rows per group",
  rowsHint: "From 3 to 10,000 rows in all.",
  seedLabel: "Seed",
  seedHint:
    "The same seed and settings always give the same data. Any text works.",
  decimalsLabel: "Decimal places",
  newSeed: "New random seed",
  includeId: "Add a row-number column",
  idName: "id",
  distributionLabel: "Distribution",
  distNormal: "Normal (bell-shaped)",
  distUniform: "Uniform (flat)",
  distSkewed: "Right-skewed (exponential)",
  distInteger: "Whole numbers (dice, scores)",
  distBinary: "Yes/no (0 and 1)",
  paramMean: "Mean",
  paramSd: "Standard deviation",
  paramMin: "Minimum",
  paramMax: "Maximum",
  paramSkewMin: "Minimum (where it starts)",
  paramSkewMean: "Mean",
  paramIntMin: "Smallest whole number",
  paramIntMax: "Largest whole number",
  paramBinary: "Probability of a 1",
  columnName: "Column name",
  defaultValue: "score",
  nameXLabel: "First variable: name",
  meanXLabel: "First variable: mean",
  sdXLabel: "First variable: SD",
  nameYLabel: "Second variable: name",
  meanYLabel: "Second variable: mean",
  sdYLabel: "Second variable: SD",
  defaultX: "study_hours",
  defaultY: "exam_score",
  rLabel: "Correlation (r) to aim for",
  rHint: "Between −1 and 1, not including either end.",
  groupNamesLabel: "Group names, separated by commas",
  groupNamesHint: "From 2 to 12 groups, each name different.",
  groupMeansLabel: "Group means, in the same order",
  groupMeansHint: "One number per group, separated by commas or semicolons.",
  groupSdLabel: "Standard deviation (the same in every group)",
  groupColumnLabel: "Group column name",
  outcomeColumnLabel: "Outcome column name",
  defaultGroup: "group",
  defaultOutcome: "outcome",
  exactLabel: "Make the sample match the settings exactly",
  exactHint:
    "Rescales the draws so the sample means, SDs and correlation equal what you typed. Handy for a clean textbook example; real samples never match exactly.",
  results: "Your dataset",
  summary: "{rows} and {columns}. Seed: {seed}.",
  rowsWord: "one:{count} row|other:{count} rows",
  columnsWord: "one:{count} column|other:{count} columns",
  summaryHeading: "What the sample looks like",
  variable: "Variable",
  group: "Group",
  count: "n",
  mean: "Mean",
  sd: "SD",
  min: "Min",
  max: "Max",
  pairNote:
    "You aimed for r = {target}; this sample's correlation is r = {actual}. Samples vary around the target — tick the exact option for a sample that hits it.",
  pairNoteExact:
    "You aimed for r = {target}, and this sample's correlation is r = {actual}.",
  groupNote:
    "Each group's sample mean differs a little from the mean you typed, as it would in real data.",
  groupNoteExact: "Each group's sample mean and SD equal the settings exactly.",
  previewHeading: "Preview",
  previewCaption:
    "The first {shown} of {total} rows, as they appear in the CSV.",
  download: "Download CSV",
  copy: "Copy CSV",
  copied: "Copied!",
  filename: "random-dataset",
  privacy:
    "Made in your browser from the seed; nothing is uploaded. Pseudo-random, for practice and demonstrations — not for security or for research claims.",
  errRows: "Enter the number of rows as a whole number from 3 to 10,000.",
  errGroupRows: "Groups × rows per group must be at most 10,000.",
  errSeed: "Enter a seed: any word or number.",
  errDecimals: "Enter the decimal places as a whole number from 0 to 8.",
  errNormal: "Enter a mean and a standard deviation greater than 0.",
  errUniform: "Enter a minimum and a larger maximum.",
  errSkewed: "Enter a minimum and a larger mean.",
  errInteger: "Enter two whole numbers, the smallest and then the largest.",
  errBinary: "Enter a probability from 0 to 1.",
  errPairSd: "Enter standard deviations greater than 0.",
  errPairMean: "Enter a mean for each variable.",
  errCorrelation: "Enter r between −1 and 1, not including either end.",
  errNames: "Give each column a different name.",
  errGroupNames:
    "Enter from 2 to 12 different group names, separated by commas.",
  errGroupMeans: "Enter one mean for each group.",
  errGroupSd: "Enter a standard deviation greater than 0.",
} as const;

type Strings = typeof RANDOM_DATASET_GENERATOR_STRINGS;

const PREVIEW_ROWS = 10;

const DIST_DEFAULTS: Record<SingleDistribution, { p1: string; p2: string }> = {
  normal: { p1: "100", p2: "15" },
  uniform: { p1: "0", p2: "100" },
  skewed: { p1: "0", p2: "10" },
  integer: { p1: "1", p2: "6" },
  binary: { p1: "0.5", p2: "" },
};

const INITIAL: DatasetForm = {
  mode: "pair",
  rows: "50",
  seed: "2024",
  decimals: "2",
  exact: false,
  includeId: false,
  distribution: "normal",
  ...DIST_DEFAULTS.normal,
  valueName: "",
  nameX: "",
  meanX: "10",
  sdX: "3",
  nameY: "",
  meanY: "70",
  sdY: "10",
  r: "0.6",
  groupColumn: "",
  outcomeColumn: "",
  groupNames: "Control, Treatment",
  groupMeans: "50, 58",
  groupSd: "10",
};

const ERROR_KEYS: Record<DatasetError, keyof Strings> = {
  rows: "errRows",
  groupRows: "errGroupRows",
  seed: "errSeed",
  decimals: "errDecimals",
  normal: "errNormal",
  uniform: "errUniform",
  skewed: "errSkewed",
  integer: "errInteger",
  binary: "errBinary",
  pairSd: "errPairSd",
  pairMean: "errPairMean",
  correlation: "errCorrelation",
  names: "errNames",
  groupNames: "errGroupNames",
  groupMeans: "errGroupMeans",
  groupSd: "errGroupSd",
};

/** Six random digits from the browser's secure generator — only ever on a click. */
function randomSeed(): string {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return String(buffer[0] % 1_000_000).padStart(6, "0");
}

function SummaryTables({
  dataset,
  info,
  strings,
  fmt,
}: {
  dataset: Dataset;
  info: DatasetInfo;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const numeric = dataset.columns.filter(
    (column) => column.kind === "number" && !column.isId
  );
  const decimals = (value: number) =>
    fmt(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rows = numeric.flatMap((column) => {
    if (column.kind !== "number") {
      return [];
    }
    const s = summarizeColumn(column);
    return [
      [
        s.name,
        fmt(s.count),
        decimals(s.mean),
        decimals(s.sd),
        decimals(s.min),
        decimals(s.max),
      ],
    ];
  });
  const [labelColumn, outcomeColumn] = dataset.columns.filter(
    (column) => !(column.kind === "number" && column.isId)
  );
  const groupRows =
    info.mode === "groups" &&
    labelColumn?.kind === "text" &&
    outcomeColumn?.kind === "number"
      ? summarizeGroups(
          labelColumn.values,
          outcomeColumn.values,
          info.groupNames
        ).map((g) => [g.name, fmt(g.count), decimals(g.mean), decimals(g.sd)])
      : [];
  return (
    <div className="space-y-3">
      <DataTable
        head={[
          strings.variable,
          strings.count,
          strings.mean,
          strings.sd,
          strings.min,
          strings.max,
        ]}
        rows={rows}
      />
      {groupRows.length > 0 ? (
        <DataTable
          head={[strings.group, strings.count, strings.mean, strings.sd]}
          rows={groupRows}
        />
      ) : null}
    </div>
  );
}

function Notes({
  dataset,
  info,
  strings,
  fmt,
}: {
  dataset: Dataset;
  info: DatasetInfo;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  if (info.mode === "pair" && info.targetR !== null) {
    const columns = dataset.columns.filter(
      (c) => c.kind === "number" && !c.isId
    );
    const [x, y] = columns;
    if (x?.kind === "number" && y?.kind === "number") {
      const three = (value: number) =>
        fmt(value, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      return (
        <Notice>
          {t(info.exact ? strings.pairNoteExact : strings.pairNote, {
            target: three(info.targetR),
            actual: three(sampleCorrelation(x.values, y.values)),
          })}
        </Notice>
      );
    }
  }
  if (info.mode === "groups") {
    return (
      <Notice>{info.exact ? strings.groupNoteExact : strings.groupNote}</Notice>
    );
  }
  return null;
}

export default function RandomDatasetGeneratorWidget() {
  const strings = useToolUi(RANDOM_DATASET_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [form, setForm] = useState<DatasetForm>(INITIAL);
  const update = (patch: Partial<DatasetForm>) =>
    setForm((current) => {
      const switched =
        patch.distribution !== undefined &&
        patch.distribution !== current.distribution
          ? DIST_DEFAULTS[patch.distribution]
          : {};
      return { ...current, ...patch, ...switched };
    });

  const result = buildDataset(form, {
    id: strings.idName,
    value: strings.defaultValue,
    x: strings.defaultX,
    y: strings.defaultY,
    group: strings.defaultGroup,
    outcome: strings.defaultOutcome,
  });
  const csv = result.ok ? toCsv(result.dataset) : "";
  const groups = form.mode === "groups";

  return (
    <>
      <section className="space-y-5">
        <PillGroup<DatasetMode>
          label={strings.modeLabel}
          name="rd-mode"
          onChange={(mode) => update({ mode })}
          options={[
            { value: "pair", label: strings.modePair },
            { value: "groups", label: strings.modeGroups },
            { value: "single", label: strings.modeSingle },
          ]}
          value={form.mode}
        />
        {form.mode === "single" ? (
          <SingleFields form={form} labels={strings} update={update} />
        ) : null}
        {form.mode === "pair" ? (
          <PairFields form={form} labels={strings} update={update} />
        ) : null}
        {groups ? (
          <GroupFields form={form} labels={strings} update={update} />
        ) : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            hint={strings.rowsHint}
            id="rd-rows"
            label={groups ? strings.rowsGroupsLabel : strings.rowsLabel}
            numeric
            onChange={(rows) => update({ rows })}
            value={form.rows}
          />
          <TextField
            id="rd-decimals"
            label={strings.decimalsLabel}
            numeric
            onChange={(decimals) => update({ decimals })}
            value={form.decimals}
          />
          <TextField
            hint={strings.seedHint}
            id="rd-seed"
            label={strings.seedLabel}
            mono
            onChange={(seed) => update({ seed })}
            value={form.seed}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {form.mode === "single" && form.distribution !== "normal" ? null : (
            <ExactToggle
              checked={form.exact}
              labels={strings}
              onChange={(exact) => update({ exact })}
            />
          )}
          <Checkbox
            checked={form.includeId}
            label={strings.includeId}
            onChange={(includeId) => update({ includeId })}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() => update({ seed: randomSeed() })}
            type="button"
          >
            {strings.newSeed}
          </button>
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result.ok ? (
          <>
            <p className="text-muted-foreground text-sm">
              {t(strings.summary, {
                rows: tPlural(strings.rowsWord, locale, result.dataset.rows, {
                  count: fmt(result.dataset.rows),
                }),
                columns: tPlural(
                  strings.columnsWord,
                  locale,
                  result.dataset.columns.length,
                  { count: fmt(result.dataset.columns.length) }
                ),
                seed: result.info.seed,
              })}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                className={BUTTON_PRIMARY}
                onClick={() =>
                  downloadText(
                    `${strings.filename}.csv`,
                    csv,
                    "text/csv;charset=utf-8"
                  )
                }
                type="button"
              >
                {strings.download}
              </button>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={csv}
              />
            </div>
            <div className={`${CARD} space-y-3`}>
              <h3 className={WIDGET_H3}>{strings.summaryHeading}</h3>
              <SummaryTables
                dataset={result.dataset}
                fmt={fmt}
                info={result.info}
                strings={strings}
              />
              <Notes
                dataset={result.dataset}
                fmt={fmt}
                info={result.info}
                strings={strings}
              />
            </div>
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.previewHeading}</h3>
              <DataTable
                caption={t(strings.previewCaption, {
                  shown: fmt(Math.min(PREVIEW_ROWS, result.dataset.rows)),
                  total: fmt(result.dataset.rows),
                })}
                head={result.dataset.columns.map((c) => c.name)}
                rows={previewRows(result.dataset, PREVIEW_ROWS)}
              />
            </div>
            <p className="text-muted-foreground text-xs">{strings.privacy}</p>
          </>
        ) : (
          <Notice>{strings[ERROR_KEYS[result.error]]}</Notice>
        )}
      </section>
    </>
  );
}
